/**
 * TwelveData-Provider.
 * Free-Tier liefert 800 Calls/Tag, 8/Min — reicht für Watchlist mit Refresh.
 *
 * Vorteile gegenüber Finnhub Free:
 * - 52W-High/Low ist bei TwelveData /quote für jede Asset-Klasse drin
 * - OHLCV-Daten via /time_series (Free!) — Pflicht für MA200, MA50, VCP-Pattern
 * - Symbol-Search liefert Exchange + Currency direkt
 */

import { DataStateHelpers } from '@/types';
import type { AssetClass, DataState } from '@/types';
import type {
  MarketDataProvider,
  OhlcvBar,
  OhlcvTimeframe,
  PriceQuote,
  SymbolLookupResult,
} from './types';

interface TwelveDataQuoteResponse {
  symbol?: string;
  name?: string;
  exchange?: string;
  currency?: string;
  open?: string;
  high?: string;
  low?: string;
  close?: string;
  volume?: string;
  previous_close?: string;
  change?: string;
  percent_change?: string;
  fifty_two_week?: {
    low?: string;
    high?: string;
  };
  code?: number;
  status?: string;
  message?: string;
}

interface TwelveDataSearchItem {
  symbol?: string;
  instrument_name?: string;
  exchange?: string;
  currency?: string;
  instrument_type?: string;
}

interface TwelveDataSearchResponse {
  data?: TwelveDataSearchItem[];
}

interface TwelveDataTimeSeriesValue {
  datetime?: string;
  open?: string;
  high?: string;
  low?: string;
  close?: string;
  volume?: string;
}

interface TwelveDataTimeSeriesResponse {
  status?: string;
  values?: TwelveDataTimeSeriesValue[];
  message?: string;
}

async function fetchJson<T>(
  url: string,
  signal: AbortSignal | undefined
): Promise<{ ok: true; data: T } | { ok: false; status: number; body: unknown }> {
  const res = await fetch(url, signal ? { signal } : {});
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    return { ok: false, status: res.status, body };
  }
  const data = (await res.json()) as T;
  return { ok: true, data };
}

function parseFloat0(s: string | undefined): number {
  if (!s) return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function classifyType(td: string | undefined): AssetClass {
  const t = (td ?? '').toLowerCase();
  if (t.includes('etf') || t.includes('fund')) return 'etf';
  if (t.includes('crypto') || t.includes('digital')) return 'crypto';
  return 'stock';
}

function tdInterval(timeframe: OhlcvTimeframe): string {
  switch (timeframe) {
    case 'daily':
      return '1day';
    case 'weekly':
      return '1week';
    case 'monthly':
      return '1month';
    default:
      return '1day';
  }
}

export const TWELVEDATA_PROVIDER: MarketDataProvider = {
  name: 'TwelveData',
  is_live: true,

  async lookupSymbol(query, signal): Promise<DataState<SymbolLookupResult[]>> {
    const trimmed = query.trim();
    if (trimmed.length === 0) return DataStateHelpers.noData();

    const result = await fetchJson<TwelveDataSearchResponse>(
      `/api/twelvedata/search?q=${encodeURIComponent(trimmed)}`,
      signal
    );

    if (!result.ok) {
      if (result.status === 503) return DataStateHelpers.noData();
      return DataStateHelpers.error({
        code: `HTTP_${result.status}`,
        message: `Symbol-Suche fehlgeschlagen (Status ${result.status}).`,
        retry_possible: result.status >= 500,
      });
    }

    const items = (result.data.data ?? [])
      .filter((r) => r.symbol)
      .map<SymbolLookupResult>((r) => ({
        symbol: (r.symbol ?? '').toUpperCase(),
        display_name: r.instrument_name ?? r.symbol ?? '',
        asset_class: classifyType(r.instrument_type),
        exchange: r.exchange ?? '',
        currency: r.currency ?? '',
      }));

    if (items.length === 0) return DataStateHelpers.noData();
    return DataStateHelpers.success(items, 300);
  },

  async getQuote(symbol, signal): Promise<DataState<PriceQuote>> {
    const symU = symbol.toUpperCase();
    const result = await fetchJson<TwelveDataQuoteResponse>(
      `/api/twelvedata/quote?symbol=${encodeURIComponent(symU)}`,
      signal
    );

    if (!result.ok) {
      if (result.status === 503) return DataStateHelpers.noData();
      return DataStateHelpers.error({
        code: `HTTP_${result.status}`,
        message: `Quote-Anfrage fehlgeschlagen (Status ${result.status}).`,
        retry_possible: result.status >= 500,
      });
    }

    const q = result.data;
    if (q.status === 'error' || q.code !== undefined) {
      return DataStateHelpers.noData();
    }

    const currentPrice = parseFloat0(q.close);
    if (currentPrice === 0) return DataStateHelpers.noData();

    const high52 = parseFloat0(q.fifty_two_week?.high);
    const low52 = parseFloat0(q.fifty_two_week?.low);
    const drawdown = high52 > 0 ? ((currentPrice - high52) / high52) * 100 : null;

    return DataStateHelpers.success(
      {
        symbol: symU,
        current_price: currentPrice,
        change_pct_today: parseFloat0(q.percent_change),
        high_52w: high52,
        low_52w: low52,
        // TwelveData /quote liefert keine Marktkapitalisierung / KGV / ROE.
        // Diese Felder bleiben null — Score-Engine muss damit umgehen.
        market_cap_billions: null,
        pe_ttm: null,
        fetched_at: new Date().toISOString(),
        return_5d_pct: null,
        return_13w_pct: null,
        return_26w_pct: null,
        return_52w_pct: null,
        roe_ttm_pct: null,
        drawdown_from_52w_high_pct: drawdown,
        industry: null,
      },
      60
    );
  },

  async getOhlcv(
    symbol: string,
    timeframe: OhlcvTimeframe,
    bars: number,
    signal?: AbortSignal
  ): Promise<DataState<OhlcvBar[]>> {
    const symU = symbol.toUpperCase();
    const interval = tdInterval(timeframe);
    const outputsize = Math.min(Math.max(bars, 30), 5000);

    const result = await fetchJson<TwelveDataTimeSeriesResponse>(
      `/api/twelvedata/time-series?symbol=${encodeURIComponent(symU)}&interval=${interval}&outputsize=${outputsize}`,
      signal
    );

    if (!result.ok) {
      if (result.status === 503) return DataStateHelpers.noData();
      return DataStateHelpers.error({
        code: `HTTP_${result.status}`,
        message: `OHLCV-Anfrage fehlgeschlagen (Status ${result.status}).`,
        retry_possible: result.status >= 500,
      });
    }

    const values = result.data.values ?? [];
    if (values.length === 0) return DataStateHelpers.noData();

    // TwelveData liefert neueste zuerst → wir drehen für chronologische Reihenfolge
    const bars_data: OhlcvBar[] = values
      .map((v) => ({
        timestamp: v.datetime ?? '',
        open: parseFloat0(v.open),
        high: parseFloat0(v.high),
        low: parseFloat0(v.low),
        close: parseFloat0(v.close),
        volume: parseFloat0(v.volume),
      }))
      .filter((b) => b.timestamp && b.close > 0)
      .reverse();

    if (bars_data.length === 0) return DataStateHelpers.noData();
    return DataStateHelpers.success(bars_data, 300);
  },
};
